/*
  Ambassador referral tagging — docs/ISSUES.md #15.

  Why this exists: a free-shipping code can never apply to an order that already ships
  free. Over $125 the carrier worker returns a $0 rate, so Shopify rejects SHAY with
  "isn't available for your shipping address" and the order carries no trace of it. The
  Flow that tags orders keys off the APPLIED discount code, so it goes quiet on exactly
  the largest orders — about a quarter of retail orders. This records the code the
  customer arrived with, independently of whether Shopify ever applied it.

  The customer arrives via /discount/SHAY — a QR code or a texted link. That attaches the
  code to the cart. Typing SHAY at checkout instead does NOT reach this script: checkout
  is Shopify-hosted and no theme JS runs there. The landing URL is what makes this work.

  Two independent paths, because neither covers everything on its own:
    - cart attribute `Referral`      order-level, the normal cart -> checkout route
    - line item property `_Referral` survives "Buy it now", which posts the product form
                                     straight to checkout and never touches the cart

  Source of truth is /cart.js, NOT the discount_code cookie: the cookie lingers after a
  code is removed and caused false positives before (see sections/rer-price-zero.liquid).
  A code that is attached but NOT applicable still appears in cart.discount_codes — that
  is precisely the state SHAY is in on a $125+ cart, and why reading the cart works where
  reading the applied discount does not.

  ----------------------------------------------------------------------------------
  THE REFERRAL IS SCOPED TO ONE BROWSER SESSION AND ONE ORDER (2026-10-13, Ryan's call).

  The first version treated the `Referral` attribute as a durable record and let it seed
  itself: `run()` read the attribute back as a fallback source for `code`, so once written
  nothing ever cleared it. It outlived the visit for the life of the Shopify cart cookie
  (~2 weeks, refreshed by every visit), kept re-injecting `_Referral` on every page load,
  and survived the customer switching to an unrelated code. That is how SQ-3236 happened:
  a scan on 2026-09-28, then a purchase nine days later of a different product paid with
  SNAPPY10 (a plain 10%-off code, nothing to do with referrals) still tagged SHAY.

  The rule now: a referral is live only in the browser session it arrived in, and it is
  spent by the first completed checkout. A customer who comes back later — to reorder or
  to buy something else — gets no tag AND no free shipping, because both the stamps and
  the code itself are revoked on sight.

  Liveness lives in sessionStorage because it is the only thing in the browser that dies
  with the tab. The cart cookie, the discount_code cookie and localStorage all survive,
  which is exactly what went wrong.

  Two things make the state machine decidable:
    - The cookie is burned on ARRIVAL. Verified 2026-10-13 against the live storefront in
      a two-arm headless run (cookie kept vs. cookie deleted, everything else identical):
      a cart-held code reaches checkout and applies with no cookie at all — the SHAY pill
      renders, shipping shows $8.00 struck through as FREE, no error banner. So burning it
      early costs the customer nothing and stops checkout resurrecting it later.
    - The `Referral` attribute doubles as the "some session already claimed this cart"
      marker. The cookie alone is not enough: the same run proved the CART keeps the code
      independently of the cookie, so "code attached + no session marker" would otherwise
      read as a fresh arrival on every return visit.
*/
(function () {
  // Codes that identify a person we want credited. Deliberately an allowlist: matching
  // any discount would tag every RER_ partner order and every sitewide promo as a
  // referral. Add ambassadors here — the Flow condition needs the same value.
  var REFERRAL_CODES = ['SHAY'];

  // Captured before the hook at the bottom of this file replaces window.fetch. Every
  // request this script makes goes through it, so observing cart traffic can never
  // re-enter and loop.
  var nativeFetch = window.fetch.bind(window);

  // Mirror of FREE_SHIP_MIN_SUBTOTAL in the carrier worker (seaqualizer-functions).
  // Above this the worker returns a $0 rate, so a free-shipping code has nothing to
  // reduce and checkout rejects it with "isn't available for your shipping address".
  // The code is therefore held only while it can actually do something. If the worker's
  // threshold moves, move this with it.
  var FREE_SHIP_MIN_SUBTOTAL = 12500;

  var ATTRIBUTE_KEY = 'Referral';
  // Leading underscore keeps it out of the customer's cart and checkout view. The theme
  // filters these in snippets/cart-drawer.liquid and sections/main-cart-items.liquid;
  // the value still reaches the order, the admin and Flow.
  var LINE_PROPERTY_KEY = '_Referral';
  var SESSION_KEY = 'sqReferralSession';

  // Version marker. A push can land in the Admin API while the storefront keeps serving
  // the previous minified asset under an unchanged ?v= hash — seen twice on this store,
  // both times right after a theme duplicate, and it once graded a verification run
  // against the OLD file. Checking the remote byte size is necessary but NOT sufficient;
  // assert this flag in the rendered page before trusting any result.
  window.__sqReferralSessionScoped = true;

  // Probed once rather than assumed: Safari private mode and some in-app browsers throw
  // on sessionStorage access. Without it there is no session boundary to detect at all,
  // so the script degrades deliberately (see claim()/run) instead of silently crediting
  // nobody or crediting forever.
  var SESSION_STORAGE_OK = (function () {
    try {
      window.sessionStorage.setItem(SESSION_KEY + ':probe', '1');
      window.sessionStorage.removeItem(SESSION_KEY + ':probe');
      return true;
    } catch (e) {
      return false;
    }
  })();

  function normalize(value) {
    var code = String(value == null ? '' : value).toUpperCase();
    return REFERRAL_CODES.indexOf(code) !== -1 ? code : null;
  }

  function matchReferral(discountCodes) {
    for (var i = 0; i < discountCodes.length; i++) {
      var code = normalize(discountCodes[i].code);
      if (code) return code;
    }
    return null;
  }

  function sessionCode() {
    if (!SESSION_STORAGE_OK) return null;
    try {
      return normalize(window.sessionStorage.getItem(SESSION_KEY));
    } catch (e) {
      return null;
    }
  }

  function claimSession(code) {
    if (!SESSION_STORAGE_OK) return false;
    try {
      window.sessionStorage.setItem(SESSION_KEY, code);
      return true;
    } catch (e) {
      return false;
    }
  }

  function endSession() {
    if (!SESSION_STORAGE_OK) return;
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (e) { /* nothing to end */ }
  }

  // Buy it now bypasses the cart entirely, so the attribute below never gets written for
  // those orders. Riding along on the line item is the only thing that survives it.
  function injectLineProperty(code) {
    var selector = 'input[name="properties[' + LINE_PROPERTY_KEY + ']"]';
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector(selector)) continue;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + LINE_PROPERTY_KEY + ']';
      input.value = code;
      forms[i].appendChild(input);
    }
  }

  // Mirror of the above, for a revoked session: a hidden input left in the DOM would
  // stamp the next Buy it now even though the referral is dead.
  function removeInjectedInputs() {
    var nodes = document.querySelectorAll('input[name="properties[' + LINE_PROPERTY_KEY + ']"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
  }

  // Returns the cookie value only when it is one of ours. An RER_ partner code lives in
  // the same cookie and drives the routing in layout/theme.liquid — never touch it.
  function readDiscountCookie() {
    var m = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
    return m ? normalize(decodeURIComponent(m[1])) : null;
  }

  // Shopify's checkout re-applies a code from the `discount_code` cookie independently of
  // cart contents, so the cookie has to go whenever the code does. It is not HttpOnly. A
  // delete must match the original's attributes, hence all three forms. Only ever called
  // for a referral code, so an RER_ partner cookie is never touched.
  function clearDiscountCookie() {
    var base = 'discount_code=; Max-Age=0; path=/';
    var host = window.location.hostname;
    var parts = host.split('.');
    document.cookie = base;                                  // host-only
    document.cookie = base + '; domain=' + host;             // explicit host
    if (parts.length > 2) {
      document.cookie = base + '; domain=.' + parts.slice(-2).join('.'); // parent domain
    }
  }

  function updateCart(payload) {
    return nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function changeLine(key, quantity, properties) {
    return function () {
      return nativeFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, properties: properties })
      });
    };
  }

  // /cart/update.js takes a single `discount` value, so "drop ours, keep theirs" has to be
  // expressed as "set the cart's code to theirs". Returns undefined when there is nothing
  // to do or when the move cannot be expressed safely — clobbering a code the customer
  // entered themselves would be a far worse bug than a stale referral.
  function discountAfterRevoke(cart) {
    var codes = cart.discount_codes || [];
    var ours = 0;
    var theirs = [];
    for (var i = 0; i < codes.length; i++) {
      if (normalize(codes[i].code)) ours++;
      else if (codes[i].code) theirs.push(codes[i].code);
    }
    if (!ours) return undefined;              // nothing of ours attached
    if (theirs.length === 0) return '';       // clear the slot outright
    if (theirs.length === 1) return theirs[0]; // keep theirs, drop ours
    return undefined;                          // two of theirs: inexpressible, leave alone
  }

  // A line item property cannot be deleted in place: the Ajax API overwrites the whole
  // properties object on every change and explicitly refuses to set it to {} ("you need to
  // remove the entire line item"). So carry the other properties across, and when ours was
  // the only one, keep the key with a blank value — Flow tests `_Referral = SHAY`, which a
  // blank no longer matches, and the theme already hides underscore keys from the cart.
  function clearLineProperties(cart) {
    var items = cart.items || [];
    var chain = Promise.resolve();
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      var props = line.properties || {};
      if (!normalize(props[LINE_PROPERTY_KEY])) continue;
      var next = {};
      var kept = 0;
      for (var key in props) {
        if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
        if (key === LINE_PROPERTY_KEY) continue;
        next[key] = props[key];
        kept++;
      }
      if (!kept) next[LINE_PROPERTY_KEY] = '';
      chain = chain.then(changeLine(line.key, line.quantity, next));
    }
    return chain;
  }

  function hasAnythingToRevoke(cart) {
    if (normalize((cart.attributes || {})[ATTRIBUTE_KEY])) return true;
    if (matchReferral(cart.discount_codes || [])) return true;
    if (readDiscountCookie()) return true;
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (normalize((items[i].properties || {})[LINE_PROPERTY_KEY])) return true;
    }
    return false;
  }

  // Erase every trace of a referral that is no longer live: the order-level stamp, the
  // line item stamps, the cookie, and the code itself. The code has to go with the rest —
  // the Flow still tags on an APPLIED SHAY code (condition A), and leaving it would hand a
  // returning customer the free shipping that belongs to the referral visit.
  function revoke(cart) {
    var payload = {};
    if (normalize((cart.attributes || {})[ATTRIBUTE_KEY])) {
      payload.attributes = {};
      payload.attributes[ATTRIBUTE_KEY] = '';
    }
    var discount = discountAfterRevoke(cart);
    if (typeof discount === 'string') payload.discount = discount;

    if (readDiscountCookie()) clearDiscountCookie();
    removeInjectedInputs();

    var started = Object.keys(payload).length ? updateCart(payload) : Promise.resolve();
    return started.then(function () { return clearLineProperties(cart); });
  }

  // Hold the code only while it can do something. Above the free-shipping threshold it
  // can't, and checkout shows the customer an error that reads like a country
  // restriction. Stripping it there is safe because the Referral attribute — not the
  // code — is what the order gets tagged from, and it survives (verified 2026-09-28).
  //
  // /cart/update.js takes a single `discount` value, so both moves are guarded on the
  // exact code count. Clearing or overwriting someone's own discount to tidy up an error
  // message would be a far worse bug than the error.
  function reconcileCode(cart, code, attached) {
    var subtotal = cart.original_total_price;
    if (typeof subtotal !== 'number') subtotal = cart.total_price || 0;
    var count = (cart.discount_codes || []).length;

    // Strip only when the referral is the ONLY thing on the cart. The cookie has to go
    // with it or checkout puts the code straight back.
    if (subtotal >= FREE_SHIP_MIN_SUBTOTAL && count === 1 && attached) {
      clearDiscountCookie();
      return updateCart({ discount: '' });
    }
    // The cookie can outlive the cart code, so clear it on any over-threshold cart that
    // still has one — otherwise checkout resurrects a code the cart no longer holds.
    if (subtotal >= FREE_SHIP_MIN_SUBTOTAL && count === 0 && readDiscountCookie()) {
      clearDiscountCookie();
      return null;
    }
    // Restore only into an empty slot. A customer who arrived via SHAY and then entered
    // SNAPPY10 has one code and no referral attached — re-applying here would silently
    // replace theirs with ours.
    if (subtotal < FREE_SHIP_MIN_SUBTOTAL && !attached && count === 0) {
      return updateCart({ discount: code });
    }
    return null;
  }

  function run() {
    nativeFetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var attached = matchReferral(cart.discount_codes || []);
        var claimed = normalize((cart.attributes || {})[ATTRIBUTE_KEY]);
        var itemCount = cart.item_count || 0;
        var code = sessionCode();

        // Spend the referral at the first completed checkout. Shopify hands back a fresh
        // empty cart with its discount code cleared, and that is the only trace of a
        // finished order the storefront ever sees. Deliberately also catches a customer
        // who empties the cart by hand — erring toward not crediting.
        if (code && !attached && itemCount === 0) {
          endSession();
          code = null;
        }

        // Arrival: the code is on the cart and this tab has never seen it. Either no
        // session has claimed the cart yet, or a live cookie says the customer has just
        // come through /discount/SHAY again — a second scan is a real referral, not a
        // stale cart, and burning the cookie on every arrival is what makes the two
        // distinguishable at all. Burn it again now: the cart alone carries the code into
        // checkout (verified), while a surviving cookie is what lets checkout resurrect
        // it weeks later.
        if (!code && attached && (!claimed || readDiscountCookie())) {
          code = attached;
          claimSession(code);
          clearDiscountCookie();
        }

        // No sessionStorage means no session boundary exists to detect. Credit the visit
        // in front of us rather than nobody, but never write the durable stamp below —
        // an unrevocable attribute is the precise failure this rewrite removes. Residual
        // gap, documented in ISSUES.md #15: in these browsers a cart that keeps the code
        // keeps crediting until the code is stripped or the cart expires.
        if (!code && attached && claimed && !SESSION_STORAGE_OK) {
          code = attached;
        }

        if (!code) {
          return hasAnythingToRevoke(cart) ? revoke(cart) : null;
        }

        injectLineProperty(code);

        // Skip a redundant write — every cart write re-renders the drawer.
        var attributes = cart.attributes || {};
        if (attributes[ATTRIBUTE_KEY] !== code && SESSION_STORAGE_OK) {
          var payload = { attributes: {} };
          payload.attributes[ATTRIBUTE_KEY] = code;
          return updateCart(payload).then(function () {
            return reconcileCode(cart, code, !!attached);
          });
        }
        return reconcileCode(cart, code, !!attached);
      })
      .catch(function () { /* attribution must never block the storefront */ });
  }

  // Running on page load alone is not enough. Dawn's cart drawer mutates the cart with
  // fetch and re-renders in place, so a customer can go product page -> Add to cart ->
  // Checkout without a single page load — and the cart crosses the $125 threshold in
  // that gap, unseen. That is the flow that kept the checkout banner alive after the
  // cookie fix (reproduced 2026-09-28: drawer-only reached checkout with the code and
  // cookie still attached and the banner showing, while the same cart via /cart was
  // clean). Observe cart mutations and reconcile again.
  //
  // Purely an observer: it hands back the original promise untouched and swallows its own
  // errors, so a failure here can never break an add to cart. Internal requests use the
  // captured nativeFetch, so this never sees its own traffic and cannot loop — including
  // the /cart/change.js writes that revocation makes.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;
  try {
    if (!window.__sqReferralFetchHooked) {
      window.__sqReferralFetchHooked = true;
      window.fetch = function (input) {
        var result = nativeFetch.apply(window, arguments);
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (CART_MUTATION.test(url)) {
            result.then(function () { setTimeout(run, 0); }, function () {});
          }
        } catch (e) { /* observation must never interfere with the cart */ }
        return result;
      };
    }
  } catch (e) { /* no hook: page-load reconciliation still applies */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
